import { useState, useCallback, useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { CytoscapeRenderer, type CytoscapeRendererHandle } from './graph/CytoscapeRenderer';
import { ObsidianGraph } from './graph/ObsidianGraph';
import { NodePopover } from './graph/NodePopover';
import { GraphToolbar } from './graph/GraphToolbar';
import { GraphLegend } from './graph/GraphLegend';
import type { ElementDefinition } from 'cytoscape';

export interface GraphPanelHandle {
  centerOnNode: (nodeId: string) => void;
  focusOnNode: (nodeId: string) => void;
}

interface Props {
  elements: ElementDefinition[];
  highlightedNodeId: string | null;
  onEntityClick?: (nodeLabel: string) => void;
  onAskCade?: (prompt: string) => void;
  graphLayers?: Map<string, ElementDefinition[]>;
  activeScope?: string;
  onScopeChange?: (scope: string) => void;
  externalFocusNodeId?: string | null;
  onExternalFocusHandled?: () => void;
  criticalPath?: string[];
  onNodeHover?: (nodeLabel: string | null, aliases?: string[]) => void;
  orientation?: 'vertical' | 'horizontal';
  /**
   * Override for the empty-state caption. Phase-4 v2 read mode passes the
   * persistent-graph caption; defaults to the v1 "Start investigating" copy.
   */
  emptyCaption?: string;
}

const MIN_PANEL_WIDTH = 280;
const DEFAULT_PANEL_WIDTH = 420;

// Feature flag: render the OBSIDIAN SVG graph by default; CytoscapeRenderer is
// kept importable as the fallback. ObsidianGraph exposes the identical
// forwardRef handle + Props, so nothing else in GraphPanel changes.
const useObsidianGraph = true;
const Renderer = useObsidianGraph ? ObsidianGraph : CytoscapeRenderer;

// Default layout — attack-path (dagre, vertical flow). Users can switch to
// 'layered' (rank by 6-layer ontology) via the toolbar; the choice persists
// to localStorage so subsequent investigations open in the same shape.
const LAYOUT_STORAGE_KEY = 'cade.graphLayout';
type SwitchableLayout = 'attack-path' | 'layered';
function readStoredLayout(): SwitchableLayout {
  try {
    const v = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (v === 'layered' || v === 'attack-path') return v;
  } catch {
    // localStorage unavailable (e.g. SSR, private mode) → fall through.
  }
  return 'attack-path';
}

export const GraphPanel = forwardRef<GraphPanelHandle, Props>(function GraphPanel({ elements, highlightedNodeId, onEntityClick, onAskCade, graphLayers, activeScope, onScopeChange, externalFocusNodeId, onExternalFocusHandled, criticalPath, onNodeHover, orientation, emptyCaption }, ref) {
  const [selectedNode, setSelectedNode] = useState<{ id: string; data: Record<string, unknown>; pos: { x: number; y: number } } | null>(null);
  const [hiddenNodes, setHiddenNodes] = useState<Set<string>>(new Set());
  // Independent of `hiddenNodes` — analysts often want to hide entire layers
  // ("don't show me any orgs") for one investigation pass without picking
  // individual nodes. Both reduce into the same `visibleElements` filter.
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const [panelHeight, setPanelHeight] = useState(Math.round(window.innerHeight * 0.4));
  const [isFocused, setIsFocused] = useState(false);
  const [showCriticalPath, setShowCriticalPath] = useState(true);
  const [layout, setLayoutState] = useState<SwitchableLayout>(() => readStoredLayout());
  const setLayout = useCallback((next: SwitchableLayout) => {
    setLayoutState(next);
    try { localStorage.setItem(LAYOUT_STORAGE_KEY, next); } catch { /* localStorage unavailable */ }
  }, []);
  // Edge labels are hover/active-only — there's no toggle (kept off by default).
  const showEdgeLabels = false;
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRendererRef = useRef<CytoscapeRendererHandle>(null);
  const draggingRef = useRef(false);
  const handleRef = useRef<HTMLDivElement>(null);
  const orientationRef = useRef(orientation);
  orientationRef.current = orientation;

  // Memoized so the renderer gets a STABLE array reference across unrelated
  // GraphPanel re-renders (selection, focus, resize). A fresh `.filter()` array
  // every render made ObsidianGraph re-run the headless layout and reset the
  // user's pan/zoom on every interaction.
  //
  // Filter pipeline: drop nodes whose id is in `hiddenNodes` OR whose type is
  // in `hiddenTypes`. Then drop any edge whose endpoints are no longer
  // visible.
  const visibleElements = useMemo(() => {
    const droppedNodeIds = new Set<string>(hiddenNodes);
    if (hiddenTypes.size > 0) {
      for (const el of elements) {
        const isNode = el.group === 'nodes' || (!el.group && !el.data.source);
        if (!isNode) continue;
        const t = el.data.type as string | undefined;
        if (t && hiddenTypes.has(t)) droppedNodeIds.add(el.data.id as string);
      }
    }
    return elements.filter((el) => {
      const isNode = el.group === 'nodes' || (!el.group && !el.data.source);
      if (isNode) return !droppedNodeIds.has(el.data.id as string);
      const src = el.data.source as string;
      const tgt = el.data.target as string;
      return !droppedNodeIds.has(src) && !droppedNodeIds.has(tgt);
    });
  }, [elements, hiddenNodes, hiddenTypes]);

  const hiddenNodeLabels = useMemo(() => {
    const labels = new Map<string, { label: string; type: string }>();
    for (const el of elements) {
      if ((el.group === 'nodes' || (!el.group && !el.data.source)) && hiddenNodes.has(el.data.id as string)) {
        labels.set(el.data.id as string, {
          label: (el.data.label as string) ?? (el.data.id as string),
          type: (el.data.type as string) ?? 'unknown',
        });
      }
    }
    return labels;
  }, [elements, hiddenNodes]);

  const handleUnhideNode = useCallback((nodeId: string) => {
    setHiddenNodes((prev) => {
      const next = new Set(prev);
      next.delete(nodeId);
      return next;
    });
  }, []);

  const handleUnhideAll = useCallback(() => {
    setHiddenNodes(new Set());
  }, []);

  const handleToggleType = useCallback((type: string) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const handleResetTypes = useCallback(() => {
    setHiddenTypes(new Set());
  }, []);

  const hasCriticalPath = (criticalPath?.length ?? 0) > 0;

  useImperativeHandle(ref, () => ({
    centerOnNode(nodeId: string) {
      cyRendererRef.current?.centerOnNode(nodeId);
      const el = elements.find(e => e.data.id === nodeId);
      if (el) handleNodeClick(nodeId, el.data as Record<string, unknown>);
    },
    focusOnNode(nodeId: string) {
      cyRendererRef.current?.focusOnNode(nodeId);
    },
  }));

  useEffect(() => {
    if (externalFocusNodeId) {
      cyRendererRef.current?.focusOnNode(externalFocusNodeId);
      onExternalFocusHandled?.();
    }
  }, [externalFocusNodeId, onExternalFocusHandled]);

  const handleNodeClick = useCallback((nodeId: string, data: Record<string, unknown>) => {
    const rect = containerRef.current?.getBoundingClientRect();
    setSelectedNode({
      id: nodeId,
      data,
      pos: { x: (rect?.width ?? 400) / 2 - 120, y: 80 },
    });
    onEntityClick?.(data.label as string);
  }, [onEntityClick]);

  const handleHideNode = useCallback((nodeId: string) => {
    setHiddenNodes((prev) => new Set(prev).add(nodeId));
  }, []);

  // Drag-to-resize logic (orientation-aware)
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      if (orientationRef.current === 'horizontal') {
        // Resize height (stacked mode — graph is on top, handle at bottom)
        const newHeight = e.clientY - 52; // 52px topbar offset
        const minH = 200;
        const maxH = window.innerHeight * 0.7;
        if (newHeight >= minH && newHeight <= maxH) setPanelHeight(newHeight);
      } else {
        // Resize width (side-by-side mode)
        const newWidth = window.innerWidth - e.clientX;
        const maxWidth = window.innerWidth * 0.7;
        if (newWidth >= MIN_PANEL_WIDTH && newWidth <= maxWidth) setPanelWidth(newWidth);
      }
    };
    const onMouseUp = () => {
      if (draggingRef.current) {
        draggingRef.current = false;
        handleRef.current?.classList.remove('dragging');
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        cyRendererRef.current?.resize();
      }
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    handleRef.current?.classList.add('dragging');
    document.body.style.cursor = orientationRef.current === 'horizontal' ? 'row-resize' : 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  return (
    <div
      className="graph-panel"
      ref={containerRef}
      style={orientation === 'horizontal' ? { height: panelHeight } : { width: panelWidth }}
    >
      <div
        ref={handleRef}
        className="graph-resize-handle"
        onMouseDown={onResizeStart}
      >
        <div className="graph-resize-grip">
          <span className="graph-resize-dot" />
          <span className="graph-resize-dot" />
          <span className="graph-resize-dot" />
        </div>
      </div>
      <GraphToolbar
        hiddenNodes={hiddenNodes}
        hiddenNodeLabels={hiddenNodeLabels}
        onUnhideNode={handleUnhideNode}
        onUnhideAll={handleUnhideAll}
        isFocused={isFocused}
        onClearFocus={() => { cyRendererRef.current?.clearFocus(); }}
        graphLayers={graphLayers}
        activeScope={activeScope}
        onScopeChange={onScopeChange}
        hasCriticalPath={hasCriticalPath}
        showCriticalPath={showCriticalPath}
        onToggleCriticalPath={() => setShowCriticalPath((prev) => !prev)}
        elements={elements}
        hiddenTypes={hiddenTypes}
        onToggleType={handleToggleType}
        onResetTypes={handleResetTypes}
        layout={layout}
        onLayoutChange={setLayout}
        onFocusNode={(nodeId) => { cyRendererRef.current?.focusOnNode(nodeId); }}
        visibleElements={visibleElements}
        onUnhideForSearch={(nodeId) => {
          // A node can be hidden via BOTH the per-node hide AND the type
          // filter at the same time. Lift whichever filter is currently
          // hiding it so the next focus call sees the node — don't short-
          // circuit after the per-node hide, that would silently leave the
          // type filter in charge and produce the same no-op the search-
          // hidden-hit fix was meant to close.
          if (hiddenNodes.has(nodeId)) handleUnhideNode(nodeId);
          const el = elements.find((e) => (e.group === 'nodes' || !e.group) && e.data.id === nodeId);
          const type = el?.data.type as string | undefined;
          if (type && hiddenTypes.has(type)) handleToggleType(type);
        }}
      />
      <div className="graph-canvas">
        {visibleElements.length > 0 ? (
          <Renderer
            ref={cyRendererRef}
            elements={visibleElements}
            layout={layout}
            onNodeClick={handleNodeClick}
            highlightedNodeId={highlightedNodeId}
            onFocusChange={setIsFocused}
            criticalPath={showCriticalPath ? criticalPath : []}
            showEdgeLabels={showEdgeLabels}
            onNodeHover={onNodeHover}
            orientation={orientation}
          />
        ) : (
          <div className="graph-empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="6" cy="12" r="2" /><circle cx="18" cy="6" r="2" /><circle cx="18" cy="18" r="2" />
              <line x1="8" y1="12" x2="16" y2="7" /><line x1="8" y1="12" x2="16" y2="17" />
            </svg>
            <p>{emptyCaption ?? 'Start investigating to build the graph'}</p>
          </div>
        )}
        {visibleElements.length > 0 && (
          <GraphLegend elements={visibleElements} />
        )}
      </div>
      {selectedNode && (
        <NodePopover
          nodeId={selectedNode.id}
          data={selectedNode.data}
          position={selectedNode.pos}
          onClose={() => setSelectedNode(null)}
          onFocus={(id) => { setSelectedNode(null); cyRendererRef.current?.focusOnNode(id); }}
          onHide={handleHideNode}
          onAskCade={onAskCade}
        />
      )}
    </div>
  );
});
